import React from 'react';
import { Link } from 'react-router-dom';
import { TooltipCellWithCopy } from 'components/common/Tables/Cells/tooltipCellWithCopy';
import { compareNumericString, onlyNumbers } from 'utilitys/helpers/general';
import colorsVars from 'styles/colors.module.scss';
import { IValidationTypeDrillDownResponse } from 'api/queries/getValidationTypeDrillDown.query';
import { ILogicCheckerCategoryResultsResponse } from 'api/queries/getLogicCheckerCategoryResults.query';
import { ILogicCheckerConfigurationResponse } from 'api/queries/getLogicCheckerConfiguration.query';

const { statusSummaryGreen, statusSummaryRed } = colorsVars;

export const passColor = statusSummaryGreen;
export const failColor = statusSummaryRed;

/**
 *
 * @param testValue
 * @param configObject
 * @return {string|string|string|string}
 */
export const getColor = (testValue, configObject) => {
    // The coloredDot wants to see a red/yellow/green string, to then choose between three gradient backgrounds
    // for text, we want the exact color that the API returns as color
    // thus we can use returnApiColor to select between these two
    // check if we actually have a config, and tresholds in the config, if not return grey
    if (!configObject || configObject?.thresholds?.length < 1) {
        return 'grey';
    }

    let upperLimitOperator = configObject.thresholds[0].upper_limit_operator;
    let upperLimit = configObject.thresholds[0].upper_limit_value;

    const low = configObject.thresholds[0].color_low_level;
    const high = configObject.thresholds[0].color_high_level;

    let lower = low === 'PASS' ? passColor : failColor;
    let upper = high === 'PASS' ? passColor : failColor;
    if (upperLimitOperator === '>=') {
        return testValue >= upperLimit ? upper : lower;
    }
    if (upperLimitOperator === '>') {
        return testValue > upperLimit ? upper : lower;
    }
    if (upperLimitOperator === '<') {
        return testValue < upperLimit ? lower : upper;
    }
    if (upperLimitOperator === '<=') {
        return testValue <= upperLimit ? lower : upper;
    }

    return 'grey';
};

export const getValidationColumns = ({
    validationTypeDrillDown,
    validation,
    check,
}: {
    validationTypeDrillDown: IValidationTypeDrillDownResponse | undefined;
    validation: ILogicCheckerConfigurationResponse | undefined;
    check: string;
}) => {
    if (validationTypeDrillDown && validation && check) {
        //  get column names
        const apiHeaders = validationTypeDrillDown.headers;

        // // make columns-array, return it
        return apiHeaders.map((column) => {
            // not all properties in a validation should map to a column
            const notValidColumns = ['activityDrilldown'];
            if (notValidColumns.includes(column.accessor)) {
                return false;
            } else {
                let generatedColumn: GeneratedColumn = {
                    Header: column.header,
                    accessor: column.accessor,
                    Filter: false,
                    colorable: column.colorable,
                    clickable: column.clickable,
                    Cell: ({ value }) => value,
                };

                // Add tooltips if needed
                const tooltipCells = ['wbs name', 'wbsName'];
                if (tooltipCells.includes(column.header.toLowerCase().trim())) {
                    generatedColumn.Cell = ({ value }) => <TooltipCellWithCopy text={value} />;
                }

                // Add widths to column, we want to add up to 1452px for level 1/2
                // and here we just add reasonable values, and calculate total width to pass to react-window later on

                let preppedAccessor = generatedColumn.accessor.trim().toLowerCase();
                if (preppedAccessor === 'range') {
                    generatedColumn.sortType = compareNumericString;
                }

                // Add color to a column based on the value in it, based on the validation config and the value of the cell
                if (column.colorable) {
                    generatedColumn.Cell = ({ value }) => {
                        // Calculate the color for this cell.
                        // For that we need the validationConfig
                        let calculatedColor = '';
                        if (check && validation) {
                            // getColor wants an int, but sometimes we are supplied a string with %, parseInt ignores % and makes an int out of it
                            let intValue = Number.parseFloat(onlyNumbers(value)); //parseInt(value);

                            calculatedColor = getColor(intValue, validation);
                        }

                        return <p style={{ color: calculatedColor, fontWeight: 'bold' }}>{value}</p>;
                    };
                }

                // Add click functionality to cell based on clickable property
                if (column.clickable) {
                    generatedColumn.Cell = ({ value, row }: { value: any; row?: any }) => {
                        const safeCheck = check.replace('/', '_');
                        return value === 0 ? (
                            value
                        ) : (
                            <Link
                                className="turquoise"
                                to={`${safeCheck}/activities?accessor=${generatedColumn.accessor}&rowId=${row.id}`}
                            >
                                {value}
                            </Link>
                        );
                    };
                }

                return generatedColumn;
            }
        });
    }

    return [];
};

interface GeneratedColumn {
    Header: any;
    accessor: any;
    Filter: boolean;
    colorable: boolean;
    clickable: boolean;
    Cell: ({ value }: { value: any }) => any;
    sortType?: any;
}

// check if current obj is 'whole project' row based on wholeProjectKeys list
/**
 * check if current row in table is type of Whole Project and needed to be uplifted
 * @param obj - row table
 * @return {this is string[]}
 */
export const isWholeProject = (obj) => {
    return Object.keys(obj).every((i) => {
        const value = obj[i];
        if (typeof value === 'string') {
            return value.toLowerCase() !== 'whole project';
        } else {
            return true;
        }
    });
};

export const getCheck = ({
    categoriesSummaryRows,
    parsedCategory,
    parsedCheck,
}: {
    categoriesSummaryRows: ILogicCheckerCategoryResultsResponse['categoriesSummaryRows'] | undefined;
    parsedCategory: string;
    parsedCheck: string;
}) => {
    if (categoriesSummaryRows && categoriesSummaryRows?.length > 0) {
        const pickedCategory = categoriesSummaryRows.find((oneCategory) => oneCategory.category === parsedCategory);
        return pickedCategory
            ? pickedCategory.validationRows.find((oneCheck) => oneCheck.validationName === parsedCheck)
            : undefined;
    }
    return undefined;
};
